import {
	TopDeskLiveInformationProvider,
	IntrestedTopDeskParty
} from "client_legacy/Workspace/LiveInformation/TopDesk/provider";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { TileContext } from "../TileContext";
import { DestinationWindow } from "client/Api/Apps/common";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { BusyIndicator, DateString } from "client/Components";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { ListItem } from "../ListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";
import { TopDeskConnectionIdentifierViewModel } from "client/Api/Apps/EditApp/saveApp";
import { IncidentListItem } from "client/Api/Workspace/LiveInformation/TopDesk/topDeskLiveInformation";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type TopDeskLiveTileContentProps = {
	provider: TopDeskLiveInformationProvider;
	onNavigate(): void;
	destinationWindow: DestinationWindow;
	connectionIdentifier: TopDeskConnectionIdentifierViewModel;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const TopDeskLiveTileContent = (props: TopDeskLiveTileContentProps): ReactElement => {
	const {
		provider,
		destinationWindow,
		connectionIdentifier,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const timezone: TimeZone = useUserTimezone();

	const {
		icon,
		id: tileId,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const navigateToHandler = useCallback(
		(url: string): void => {
			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[destinationWindow]
	);

	const tileRefreshHandler = useCallback(
		(): void => {
			provider.forceReload();
		},
		[provider]
	);

	const titleSuffix = useMemo(
		(): string | undefined => {
			if (loadingState.isLoading
				|| loadingState.hasGlobalFailure
				|| loadingState.hasTooManyConsecutiveErrors
				|| loadingState.isNotConfigured
			) {
				return undefined;
			}

			const { items, hasMore } = loadingState.response;

			let amountText: string = items.length.toString();
			if (hasMore) {
				amountText += "+";
			}

			return `(${amountText})`;
		},
		[loadingState]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[addAnalyticsEvent, onNavigate]
	);

	const itemClickHandler = useCallback(
		(item: IncidentListItem): void => {
			addAnalyticsEvent();
			navigateToHandler(item.selfServiceUrl);
		},
		[addAnalyticsEvent, navigateToHandler]
	);

	useEffect(
		(): (() => void) => {
			setLoadingState({ isLoading: true });

			const party: IntrestedTopDeskParty = {
				partyId: tileId,
				resourceId: connectionIdentifier,
				informAboutTopDeskNotBeingConfigured: () => {
					setLoadingState({
						isLoading: false,
						isNotConfigured: true
					});
				},
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateItems: (response: LazyLoadPageResult<IncidentListItem>) => {
					setLoadingState({
						isLoading: false,
						response
					});
				}
			};

			provider.register(party, { items: true });

			return (): void => provider.unregister(party);
		},
		[
			provider,
			connectionIdentifier,
			tileId
		]
	);

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		const {
			hasGlobalFailure,
			hasTooManyConsecutiveErrors,
			isNotConfigured
		} = loadingState;

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasGlobalFailure) {
			return <LiveTileContent.Message text={localize("_ErrorDuringTicketsRetrieval")} />;
		}

		if (isNotConfigured) {
			return <LiveTileContent.Message text={localize("_IncompleteConfigurationPleaseContactYourAdmin")} />;
		}

		const {
			items
		} = loadingState.response;

		if (items.length === 0) {
			return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{items.map((x): ReactElement => {
						const descriptionToShow: string = x.briefDescription || x.number;

						return (
							<ListItem
								key={x.id}
								trackingId={generateTrackingId("incident", `|${x.id}`)}
								isHoverable
								onClick={() => itemClickHandler(x)}
								title={descriptionToShow}
							>
								<ListItem.Content>
									<ListItem.Row>
										<ListItem.Title>
											{descriptionToShow}
										</ListItem.Title>
									</ListItem.Row>

									<ListItem.Row>
										<ListItem.Info>
											{x.processingStatus || ""}
										</ListItem.Info>
										<ListItem.RightAddition>
											<ListItem.Info>
												<DateString
													value={x.creationDate}
													format="AbsoluteTimestamp"
													timezone={timezone}
												/>
											</ListItem.Info>
										</ListItem.RightAddition>
									</ListItem.Row>
								</ListItem.Content>
							</ListItem>
						);
					})}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.Title
					text={localize("_MyOpenIncidents")}
					suffix={titleSuffix}
				/>
			</LiveTileContent.Header>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>
			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};

TopDeskLiveTileContent.displayName = "TopDeskLiveTileContent";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
	{
		hasTooManyConsecutiveErrors: true;
		hasGlobalFailure?: undefined;
		isNotConfigured?: undefined;
	}
	|
	{
		hasGlobalFailure: true;
		hasTooManyConsecutiveErrors?: undefined;
		isNotConfigured?: undefined;
	}
	|
	{
		isNotConfigured: true;
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
	}
	|
	{
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
		isNotConfigured?: undefined;
		response: LazyLoadPageResult<IncidentListItem>;
	}
);
